import { Card, CardContent, Grid, Typography } from "@mui/material";
import Moment from "react-moment";

import Chip from "../../../../common/Chip/Chips";
import PatientCardInfo from "../../PatientCardInfo";
import ImageAsset from "../ImageAsset";
import PatientDetail from "../PatientDetail";

import { responseStatus, userRoles } from "../../../../../constants/user";

import styles from "../../styles.module.scss";
import { formatAge, formatCurrency } from "../utils";
import { useMemo } from "react";
import { useAppSelector } from "../../../../../redux/store";

type Props = {
  FOLLOW_UP_ARRAY: string[];
  patient: any;
};

const PatientAdmissionInformation = (props: Props) => {
  const { FOLLOW_UP_ARRAY, patient } = props;
  const { role } = useAppSelector((state) => {
    return state.auth.profileData.user;
  });

  const billAmount = useMemo(() => {
    return (
      patient?.billSummary?.billAmount?.toLocaleString("en-IN", {
        maximumFractionDigits: 2,
        style: "currency",
        currency: "INR",
      }) || ""
    );
  }, [patient?.billSummary?.billAmount]);

  const closedBillAmount = useMemo(() => {
    return (
      patient?.billSummary?.billAmount?.toLocaleString("en-IN", {
        maximumFractionDigits: 2,
        style: "currency",
        currency: "INR",
      }) || ""
    );
  }, [patient?.billSummary?.closedBillAmount]);

  return (
    <Grid item xs={12} lg={5}>
      <div className={styles.modalLeftPanel}>
        <PatientCardInfo title="Status:">
          <Chip label={patient.status} />{" "}
          {FOLLOW_UP_ARRAY.includes(patient.status) && (
            <h6 className="heading6 mg-r-10 mg-l-10">
              (
              {patient.isResponded
                ? responseStatus.RESPONDED
                : responseStatus.NOT_RESPONDED}
              )
            </h6>
          )}
        </PatientCardInfo>
        {patient?.uhid && (
          <PatientCardInfo title="UHID Number:">
            <strong className="font-weight-semi-bold">
              {patient?.uhid?.toUpperCase()}
            </strong>
          </PatientCardInfo>
        )}
        {patient?.admittedUnderDr && (
          <Card variant="outlined" className={`${styles.card} mg-b-10`}>
            <CardContent className={styles.cardContent}>
              <h6 className="heading6 mg-r-10">Admission Info</h6>
              <div className="mg-t-10">
                <p>
                  <span className="font-size-14 mg-r-10">Doctor:</span>
                  <strong className="font-weight-semi-bold">
                    {patient?.admittedUnderDr?.toUpperCase()}
                  </strong>
                </p>
                <div className="mg-t-6">
                  <span className="font-size-14 mg-r-15">Admitted On:</span>
                  <strong className="font-weight-semi-bold">
                    <Moment
                      format="MMM DD, YYYY"
                      date={patient?.uhidAssignedDate?.replace("Z", "")}
                    />
                  </strong>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {patient?.ipNumber && (
          <PatientCardInfo title="IP Number:">
            <strong className="font-weight-semi-bold">
              {patient?.ipNumber}
            </strong>
          </PatientCardInfo>
        )}
        <Typography variant="h6" sx={{ margin: "20px 0" }} component="h2">
          Details
        </Typography>
        <PatientDetail heading="Name" description={patient?.name} />
        <PatientDetail heading="Age" description={formatAge(patient?.age)} />
        <PatientDetail heading="Gender" description={patient?.sex} />
        <PatientDetail
          heading="Parent Name"
          description={patient?.parentsName}
        />
        <PatientDetail heading="Contact Phone" description={patient?.phone} />
        <PatientDetail heading="Email" description={patient?.email} />
        <PatientDetail heading="Address" description={patient?.address} />
        <PatientDetail heading="District" description={patient?.district} />
        <div className="mb-16">
          <h3 className={styles.modalSubheading}>Id proof</h3>
          <Grid container spacing={1}>
            {patient?.card?.map((cardInfo: any) => {
              return cardInfo ? (
                <Grid key={cardInfo?._id} item xs={12} md={4}>
                  <ImageAsset imgUrl={cardInfo?.path} title={cardInfo?.label} />
                </Grid>
              ) : null;
            })}
          </Grid>
        </div>
        <PatientDetail
          heading="Medical history brief"
          description={patient?.medicalHistory}
        />
        <PatientDetail
          heading="Referring doctor name"
          description={patient?.lastTreatingDrNmae}
        />
        <PatientDetail
          heading="Referred Department"
          description={`${patient?.department || ""}`}
        />

        <PatientDetail
          heading="Treatment"
          description={`${patient?.diseaseGroup || ""}`}
        />
        <PatientDetail
          heading="Lower Income Beneficiary Status"
          description={patient?.isLowerIncomeBeneficiary ? "Eligible" : "N/A"}
        />
        {patient?.isLowerIncomeBeneficiary && (
          <div className="mb-16">
            <h3 className={styles.modalSubheading}>
              Lower income certificate (from CO office)
            </h3>
            <Grid container spacing={1}>
              <Grid item xs={12} md={4}>
                <ImageAsset
                  imgUrl={patient.incomeCertificate}
                  title={"Income Certificate"}
                />
              </Grid>
            </Grid>
          </div>
        )}
        <div className="mb-16">
          <h3 className={styles.modalSubheading}>Supportive documents</h3>
          <Grid container spacing={1}>
            {patient?.supportiveDocs?.map((doc: any) => {
              return (
                <Grid key={doc?._id} item xs={12} md={4}>
                  <ImageAsset imgUrl={doc?.path} title={doc?.label} />
                </Grid>
              );
            })}
          </Grid>
        </div>
        <PatientDetail
          heading="Bill Amount"
          description={`${
            formatCurrency(patient?.billSummary?.billAmount) || ""
          }`}
        />

        <PatientDetail
          heading="Closed Bill Amount"
          description={`${
            formatCurrency(patient?.billSummary?.closedBillAmount) || ""
          }`}
        />

        <PatientDetail
          heading="Bill No"
          description={`${patient?.billSummary?.billNumber || ""}`}
        />

        {patient?.billSummary?.billDocuments?.length > 0 && (
          <div className="mb-16">
            <h3 className={styles.modalSubheading}>Bill Documents</h3>
            <Grid container spacing={1}>
              {patient?.billSummary?.billDocuments?.map(
                (url: string, index: number) => {
                  return (
                    <Grid key={url} item xs={12} md={4}>
                      <ImageAsset
                        imgUrl={url}
                        title={"Document " + (index + 1)}
                      />
                    </Grid>
                  );
                }
              )}
            </Grid>
          </div>
        )}

        {[userRoles.D_C, userRoles.STAFF, userRoles.SUPER_ADMIN].includes(
          role
        ) &&
          patient?.estimateApprovalDocs?.length > 0 && (
            <div className="mb-16">
              <h3 className={styles.modalSubheading}>
                Estimate Approval Documents
              </h3>
              <Grid container spacing={1}>
                {patient?.estimateApprovalDocs?.map((doc: any) => {
                  return (
                    <Grid key={doc?._id} item xs={12} md={4}>
                      <ImageAsset imgUrl={doc?.path} title={doc?.label} />
                    </Grid>
                  );
                })}
              </Grid>
            </div>
          )}

        {[userRoles.D_C, userRoles.STAFF, userRoles.SUPER_ADMIN].includes(
          role
        ) &&
          patient?.enhanceApprovalDocs?.length > 0 && (
            <div className="mb-16">
              <h3 className={styles.modalSubheading}>
                Enhance Approval Documents
              </h3>
              <Grid container spacing={1}>
                {patient?.enhanceApprovalDocs?.map((doc: any) => {
                  return (
                    <Grid key={doc?._id} item xs={12} md={4}>
                      <ImageAsset imgUrl={doc?.path} title={doc?.label} />
                    </Grid>
                  );
                })}
              </Grid>
            </div>
          )}
      </div>
    </Grid>
  );
};

export default PatientAdmissionInformation;
